import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ExternalEoiService } from '@services/api/external-eoi.service';
import { ToasterService } from '@services/util/toaster.service';
import {
  EXTERNAL_EOI_FIELD_CATALOG,
  ExternalEoiCatalogField,
  ExternalEoiConfigField,
  ExternalEoiLink,
  buildExternalEoiShareUrl,
  catalogToConfigField,
} from '@modules/my-projects/edit-project/project-edit/external-eoi/external-eoi.model';

/** How the agent is naming the buyer on this link. */
export type ExternalEoiBuyerMode = 'existing' | 'lead' | 'new';

/**
 * ExternalEoiGenerateModalComponent
 *
 * The "Send External EOI Form" dialog, extracted so a link can be generated
 * from anywhere that has a project in hand — the project's External EOI tab,
 * an EOI record, or a lead. Callers pass whatever context they already know
 * (lot / contact / lead / EOI) and it is sent straight through to
 * `POST /external-eoi/links`.
 *
 * Passing `editLink` switches the dialog to editing that link's details in
 * place (`PATCH /external-eoi/links/:id`) — the URL the buyer already has
 * keeps working, which is the difference between this and "regenerate".
 */
@Component({
  selector: 'app-external-eoi-generate-modal',
  templateUrl: './external-eoi-generate-modal.component.html',
  styleUrls: ['./external-eoi-generate-modal.component.scss'],
})
export class ExternalEoiGenerateModalComponent implements OnChanges {
  /** Show/hide the dialog. */
  @Input() visible = false;

  @Input() projectId: string | null = null;
  @Input() projectName = '';

  /** Preselected lot — when set, the lot picker is locked to it. */
  @Input() lotId: string | null = null;
  @Input() lotName = '';

  /** Preselected contact, e.g. the lead's or EOI's buyer. */
  @Input() contactId: string | null = null;
  @Input() contactName = '';
  @Input() contactEmail = '';
  @Input() contactMobile = '';

  /** Origin record ids — passed through so the backend can link them up. */
  @Input() leadId: string | null = null;
  @Input() eoiId: string | null = null;

  /** Default link lifetime in days. */
  @Input() defaultExpiryDays = 30;

  /** Preset the deposit answer where the caller already knows it. */
  @Input() defaultDepositReceived: boolean | null = null;

  /** When set, the dialog edits this link instead of creating a new one. */
  @Input() editLink: ExternalEoiLink | null = null;

  @Output() closed = new EventEmitter<void>();
  @Output() generated = new EventEmitter<any>();

  lots: any[] = [];
  lotsLoading = false;
  generating = false;

  /** Only used when the caller has no project of its own (e.g. a contact). */
  projects: any[] = [];
  projectsLoading = false;
  genProjectId: string | null = null;

  genLotId: string | null = null;
  genContact: any = null;
  genContactIds: any[] = [];

  /**
   * `p-calendar` (used for every other date field in the app, e.g. the EOI editor's Cancelled
   * Date) is bound directly to this `Date | null` field — NOT a getter. A getter re-evaluated on
   * every change-detection pass hands `p-calendar` a brand-new `Date` object each time even when
   * the logical date hasn't changed, which it treats as a real input change; that retriggers CD,
   * which reads the getter again, forever — the dialog would open and immediately hang the tab.
   * Converted to/from the 'yyyy-MM-dd' string the backend wants only at the two edges (`reset()`
   * below, and `expires_at` in `buildPayload`), via `parseIsoDate`/`toIsoDate`, both in local time
   * rather than `Date`'s UTC-based constructor/`toISOString()` — those can land a date-only string
   * on the wrong day for any non-UTC user.
   */
  genExpiryDateValue: Date | null = null;

  private parseIsoDate(s: string): Date | null {
    if (!s) return null;
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  private toIsoDate(value: Date | null): string {
    if (!value) return '';
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  /**
   * Buyer entry mode. An agent either picks an existing contact, picks a lead,
   * or types a new purchaser's details straight in.
   */
  buyerMode: ExternalEoiBuyerMode = 'existing';
  newBuyerName = '';
  newBuyerEmail = '';
  newBuyerMobile = '';

  /**
   * Has the $2,000 initial deposit been received? `null` = agent hasn't said.
   * The lot must not be reserved until this is true, and the buyer form warns
   * that submitting does not reserve the property while it is not.
   */
  depositReceived: boolean | null = null;

  /**
   * Extra fields for just this link, on top of the project's standard template — e.g. a document
   * only this buyer needs to upload. Catalogue-sourced, same as the project template's own fields.
   */
  readonly extraFieldCatalog = EXTERNAL_EOI_FIELD_CATALOG;
  selectedExtraKeys: string[] = [];

  /** Catalogue keys already on the project's own template — excluded below since every link already carries them. */
  private projectTemplateKeys = new Set<string>();

  /**
   * The picker's offering: the full catalogue minus whatever the project template already
   * includes.
   *
   * Rebuilt explicitly whenever `projectTemplateKeys` actually changes, rather than exposed as a
   * getter — ng-select's `[items]` re-processes the array on every reference change, and a getter
   * hands it a brand-new array on every change-detection tick. That reset ng-select's internal
   * option list mid-click, so a click on a field here never landed as a selection.
   */
  public availableExtraFields: ExternalEoiCatalogField[] = [...this.extraFieldCatalog];

  private rebuildAvailableExtraFields(): void {
    this.availableExtraFields = this.extraFieldCatalog.filter(c => !this.projectTemplateKeys.has(c.key));
  }

  /** Resolves the picked keys to full config-field objects, in the order they were picked. */
  private buildExtraFields(): ExternalEoiConfigField[] {
    return this.selectedExtraKeys
      .map((key, i) => {
        const cat = this.extraFieldCatalog.find(c => c.key === key);
        return cat ? catalogToConfigField(cat, i) : null;
      })
      .filter((f): f is ExternalEoiConfigField => !!f);
  }

  constructor(
    private externalEoi: ExternalEoiService,
    private toaster: ToasterService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['visible'] || changes['editLink']) && this.visible) this.reset();
  }

  /** True when the dialog is amending an existing link rather than creating one. */
  get isEditing(): boolean {
    return !!this.editLink?._id;
  }

  get title(): string {
    return this.isEditing ? 'Edit External EOI Link' : 'Send External EOI Form';
  }

  get submitLabel(): string {
    if (this.generating) return this.isEditing ? 'Saving…' : 'Generating…';
    return this.isEditing ? 'Save Changes' : 'Generate & Copy Link';
  }

  /** Seed the dialog from the caller's context (or the link being edited). */
  private reset(): void {
    const link = this.editLink;

    // Editing seeds from the link; creating seeds from the host's context.
    this.genLotId = link?.lot_id || this.lotId || null;

    const seedContactId = link?.contact_id || this.contactId || null;
    const seedName = link?.buyer_name || this.contactName || '';
    const seedEmail = link?.buyer_email || this.contactEmail || '';
    const seedMobile = link?.buyer_mobile || this.contactMobile || '';

    this.genContact = seedContactId ? { _id: seedContactId, full_name: seedName, email: seedEmail, mobile_no: seedMobile } : null;
    this.genContactIds = seedContactId ? [seedContactId] : [];

    // A link generated from a lead already knows its buyer — there is no
    // reason to make the agent find that person again, so their details are
    // carried into the "new purchaser" fields when there is no contact record.
    const hasNamedBuyerWithoutContact = !seedContactId && !!seedName;
    this.buyerMode = hasNamedBuyerWithoutContact ? 'new' : 'existing';
    this.newBuyerName = hasNamedBuyerWithoutContact ? seedName : '';
    this.newBuyerEmail = hasNamedBuyerWithoutContact ? seedEmail : '';
    this.newBuyerMobile = hasNamedBuyerWithoutContact ? seedMobile : '';

    this.depositReceived = link ? (link.deposit_received ?? null) : this.defaultDepositReceived;
    this.selectedExtraKeys = (link?.extra_fields || []).map(f => f.key);

    if (link?.expires_at) {
      this.genExpiryDateValue = this.parseIsoDate(String(link.expires_at).slice(0, 10));
    } else {
      const d = new Date();
      d.setDate(d.getDate() + (this.defaultExpiryDays || 30));
      this.genExpiryDateValue = d;
    }

    this.genProjectId = link?.project_id || this.projectId || null;
    const projectForLots = this.effectiveProjectId;
    if (projectForLots) {
      // Editing always needs the list so the lot can be changed, even though a
      // creating caller with a fixed lot does not.
      if (this.isEditing || !this.lotId) this.loadLots(projectForLots);
      this.loadProjectTemplateKeys(projectForLots);
    } else {
      // No project from the caller — let the agent pick one.
      this.loadProjects();
      this.projectTemplateKeys = new Set();
      this.rebuildAvailableExtraFields();
    }
  }

  /**
   * Which catalogue keys the project's own template already carries, so the "extra fields" picker
   * below only offers what isn't already on every link. Best-effort: a project with no saved
   * template yet (or a lookup that fails) just offers the full catalogue rather than blocking the
   * picker on it.
   */
  private loadProjectTemplateKeys(projectId: string): void {
    this.externalEoi.getConfig(projectId).subscribe({
      next: (res: any) => {
        const keys = (res?.data?.fields || []).map((f: any) => f?.key).filter(Boolean);
        this.projectTemplateKeys = new Set(keys);
        this.rebuildAvailableExtraFields();
      },
      error: () => {
        this.projectTemplateKeys = new Set();
        this.rebuildAvailableExtraFields();
      },
    });
  }

  /** The project the link will be scoped to: the caller's, or the one picked here. */
  get effectiveProjectId(): string | null {
    return this.editLink?.project_id || this.projectId || this.genProjectId;
  }

  /** True when the caller gave no project, so the dialog must ask for one. */
  get needsProjectPicker(): boolean {
    return !this.projectId && !this.editLink?.project_id;
  }

  /** The lot picker is locked only when creating from a record that fixed it. */
  get lotLocked(): boolean {
    return !this.isEditing && !!this.lotId;
  }

  /**
   * True when the dialog was opened from a record that already identifies the buyer — a lead or
   * an EOI. Asking the agent to search for the person they just came from is busywork, so the
   * pickers are replaced with a read-only summary of who the link is for.
   *
   * Deliberately not gated on `contactId`: a lead is very often not a contact yet, and those are
   * exactly the cases where re-picking the person from the lead list silently dropped their email
   * address, since that list does not return one.
   *
   * Editing is excluded — changing who a link is for is a legitimate reason to open it.
   */
  get hasPrefilledBuyer(): boolean {
    return !this.isEditing && !!(this.contactName || this.contactEmail);
  }

  /** Who the link is for, when the caller already knows. */
  get prefilledBuyerName(): string {
    return this.contactName || this.contactEmail || 'Selected buyer';
  }

  private loadProjects(): void {
    this.projectsLoading = true;
    this.externalEoi.getProjects().subscribe({
      next: (res: any) => {
        this.projects = (res?.data || []).sort((a: any, b: any) => (a?.Project_Name || '').trim().localeCompare((b?.Project_Name || '').trim()));
        this.projectsLoading = false;
      },
      error: () => {
        this.projects = [];
        this.projectsLoading = false;
      },
    });
  }

  /** Picking a project re-scopes the lot list. */
  onProjectChange(): void {
    this.genLotId = null;
    this.lots = [];
    // A field selected as "extra" under the previous project may already be standard on the new
    // one's template (or vice versa) — cleared rather than carried over silently.
    this.selectedExtraKeys = [];
    this.projectTemplateKeys = new Set();
    this.rebuildAvailableExtraFields();
    if (this.genProjectId) {
      this.loadLots(this.genProjectId);
      this.loadProjectTemplateKeys(this.genProjectId);
    }
  }

  /**
   * The buyer's email — needed because the OTP is sent there.
   *
   * A buyer picked through the shared multiselect arrives in that component's own shape, and a
   * lead picked from the list carries no address at all because the list endpoint does not return
   * one. Falling back to the address the caller seeded us with stops a buyer who plainly has an
   * email from being reported as having none — but only when the pick is still that same person,
   * so switching to someone else does not inherit the previous buyer's address.
   */
  get buyerEmail(): string {
    if (this.buyerMode === 'new') return (this.newBuyerEmail || '').trim();
    const picked = this.genContact;
    const fromPick = picked?.email || picked?.email_address || picked?.primary_email || '';
    return String(fromPick || (this.pickIsSeededBuyer ? this.contactEmail : '') || '').trim();
  }

  /** True while the picked buyer is still the one the caller opened the dialog with. */
  private get pickIsSeededBuyer(): boolean {
    const pickedId = this.genContact?._id;
    if (!pickedId) return !this.genContact && this.hasPrefilledBuyer;
    return pickedId === this.contactId || pickedId === this.leadId;
  }

  /** Without an email the buyer cannot receive the one-time passcode. */
  get missingBuyerEmail(): boolean {
    return !this.buyerEmail;
  }

  /**
   * True once the agent has actually named a buyer — by picking one or typing one in.
   *
   * An untouched dialog has no email either, so without this the warning greeted the agent before
   * they had chosen anyone: an error about a field they had not reached yet.
   */
  get hasSelectedBuyer(): boolean {
    if (this.hasPrefilledBuyer) return true;
    if (this.buyerMode === 'new') return !!(this.newBuyerName.trim() || this.newBuyerEmail.trim());
    return !!this.genContact;
  }

  /** Only warn about a missing address once there is a buyer it could belong to. */
  get showMissingBuyerEmail(): boolean {
    return this.hasSelectedBuyer && this.missingBuyerEmail;
  }

  /**
   * Link statuses that no longer represent a claim on a lot. Mirrors `ExternalEoiComponent`'s own
   * `INACTIVE_STATUSES` — anything else (generated, awaiting buyer, submitted, reviewed, ...) means
   * somebody is already in the pipeline for that lot.
   */
  private static readonly INACTIVE_LINK_STATUSES = new Set(['cancelled', 'expired']);

  private loadLots(projectId: string): void {
    if (!projectId) return;
    this.lotsLoading = true;
    forkJoin({
      lots: this.externalEoi.getProjectLots(projectId),
      // A lot's own status_reason (checked by isLotSelectable below) is not always kept in sync
      // once a link is generated against it — reported live: a lot with a submitted EOI was still
      // offered here because `/unit-short-detail` still read it as "For Sale". Cross-referencing
      // the project's own links catches that even when the lot record hasn't caught up. Best-effort
      // — a failed links fetch falls back to the status filter alone rather than blocking the lot
      // list on it.
      links: this.externalEoi.getLinks(projectId).pipe(catchError(() => of({ data: [] }))),
    }).subscribe({
      next: ({ lots, links }: any) => {
        const takenLotIds = new Set(
          (links?.data || [])
            .filter((l: any) => l?.lot_id && l._id !== this.editLink?._id && !ExternalEoiGenerateModalComponent.INACTIVE_LINK_STATUSES.has(l.status))
            .map((l: any) => l.lot_id)
        );
        // Lots are numbered, so a plain string sort puts "10" before "2".
        // Numeric collation keeps the dropdown in the order an agent reads it.
        this.lots = [...(lots?.data || [])]
          .filter((lot: any) => this.isLotSelectable(lot) && !takenLotIds.has(lot?._id))
          // ng-select binds a single label field, so the "name or number"
          // fallback is resolved here rather than in the template.
          .map((lot: any) => ({ ...lot, lot_label: String(lot?.lot_name || lot?.lot_number || '').trim() || 'Unnamed lot' }))
          .sort((a: any, b: any) => a.lot_label.localeCompare(b.lot_label, undefined, { numeric: true, sensitivity: 'base' }));
        this.lotsLoading = false;
      },
      error: () => {
        this.lots = [];
        this.lotsLoading = false;
      },
    });
  }

  /**
   * Lot statuses a buyer can still express interest in. Anything sold, held or otherwise off the
   * market would only produce an EOI the agent has to unpick. Matched case-insensitively because
   * these are agent-editable tag names. Backend hasn't yet confirmed the authoritative list
   * (docs/external-eoi-backend-requirements.md P2-5) — this is still the same working assumption,
   * unchanged.
   */
  private static readonly SELECTABLE_LOT_STATUSES = ['for sale', 'available', 'released'];

  /**
   * Keeps a lot whose status is sellable.
   *
   * `/unit-short-detail` carries the sales state on `status_reason` — not `status`. Reading the
   * wrong key meant every lot fell through to the "no status, keep it" branch below, so sold and
   * held lots were offered for a new EOI.
   *
   * A blank status now EXCLUDES the lot rather than keeping it — reported live: a held lot with
   * (presumably) no `status_reason` was still offered for a new EOI. It previously defaulted to
   * "keep" on the reasoning that older projects predate the field and hiding those would look like
   * missing data, but an agent taking a deposit on a held lot is the worse failure of the two, so
   * unknown status now reads as "not confirmed sellable" instead. See P2-5 for the real fix —
   * server-side filtering, so this doesn't have to guess at a lot's status from a display tag.
   */
  private isLotSelectable(lot: any): boolean {
    const status = (lot?.status_reason ?? lot?.status ?? lot?.lot_status ?? '').toString().trim().toLowerCase();
    return ExternalEoiGenerateModalComponent.SELECTABLE_LOT_STATUSES.includes(status);
  }

  onContactSelect(contact: any): void {
    this.genContact = contact || null;
  }

  onContactCreate(event: any): void {
    this.genContact = event || null;
  }

  /** Switching between contact and lead search clears the previous pick. */
  setBuyerMode(mode: ExternalEoiBuyerMode): void {
    if (this.buyerMode === mode) return;
    this.buyerMode = mode;
    if (mode !== 'new') {
      this.genContact = null;
      this.genContactIds = [];
    }
  }

  close(): void {
    this.closed.emit();
  }

  /**
   * Close only when the click lands on the backdrop itself, not on the panel.
   * We deliberately avoid stopPropagation on the panel so inner clicks still
   * bubble to document — the contact picker's click-outside needs them.
   */
  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) this.close();
  }

  /**
   * True while the "this buyer has no email" confirmation is on screen.
   *
   * A link for a buyer with no address is not merely imperfect — it is unusable. Access is gated
   * behind a passcode that can only be emailed, so the buyer would open the link and be stuck at
   * the verification step with nothing to verify against. Worth stopping for rather than letting
   * the agent send a dead link and find out from the buyer.
   */
  showNoEmailConfirm = false;

  /** Set once the agent has accepted the warning, so the second attempt goes straight through. */
  private noEmailAcknowledged = false;

  confirmGenerateWithoutEmail(): void {
    this.noEmailAcknowledged = true;
    this.showNoEmailConfirm = false;
    this.generate();
  }

  cancelGenerateWithoutEmail(): void {
    this.showNoEmailConfirm = false;
  }

  generate(): void {
    const projectId = this.effectiveProjectId;
    if (!projectId) {
      this.toaster.Error('Select a project before generating a link.');
      return;
    }
    // The lot is deliberately optional — a form is often sent before the buyer
    // has settled on one, and `lot_id` then travels as null.
    if (this.buyerMode === 'new' && !this.newBuyerName.trim()) {
      this.toaster.Error("Enter the purchaser's name.");
      return;
    }
    // A link for a buyer with no email is not merely imperfect — the passcode can only be emailed,
    // so they would reach the verification step with nothing to verify against. Asked once, then
    // remembered: re-prompting on every click would just train the agent to dismiss it.
    if (this.missingBuyerEmail && !this.noEmailAcknowledged) {
      this.showNoEmailConfirm = true;
      return;
    }

    this.generating = true;
    const usingNewBuyer = this.buyerMode === 'new';
    const payload: any = {
      project_id: projectId,
      lot_id: this.genLotId,
      // A typed-in purchaser has no contact record yet — the backend resolves or
      // creates one from these details on submit.
      contact_id: usingNewBuyer ? null : this.genContact?._id || null,
      buyer_name: usingNewBuyer ? this.newBuyerName.trim() || null : this.genContact?.full_name || this.genContact?.name || this.contactName || null,
      // Resolved rather than read straight off the pick, so a lead chosen from a
      // list that returns no addresses still travels with the one we were given.
      buyer_email: this.buyerEmail || null,
      buyer_mobile: usingNewBuyer ? this.newBuyerMobile.trim() || null : this.genContact?.mobile_no || null,
      // A lead picked here is still the origin record, so it travels either way.
      lead_id: (this.buyerMode === 'lead' ? this.genContact?._id : null) || this.editLink?.lead_id || this.leadId || null,
      eoi_id: this.editLink?.eoi_id || this.eoiId || null,
      deposit_received: this.depositReceived === null ? null : !!this.depositReceived,
      expires_at: this.toIsoDate(this.genExpiryDateValue) || null,
      // Sent as an array (possibly empty) rather than omitted: editing a link to remove its last
      // extra field must clear it on the backend, not leave the previous selection untouched.
      extra_fields: this.buildExtraFields(),
    };

    const request = this.isEditing ? this.externalEoi.updateLink(this.editLink!._id!, payload) : this.externalEoi.generateLink(payload);

    request.subscribe({
      next: (res: any) => {
        this.generating = false;
        const link = res?.data;
        if (this.isEditing) {
          this.toaster.Success('External EOI link updated.');
        } else {
          // Rebuilt against the current origin — the backend url may be
          // hash-routed or point at another environment.
          const url = buildExternalEoiShareUrl(link, window.location.origin);
          if (url) {
            this.copyToClipboard(url);
            this.toaster.Success('External EOI link generated and copied to clipboard.');
          } else {
            this.toaster.Success('External EOI link generated.');
          }
        }
        this.generated.emit(link);
        this.closed.emit();
      },
      error: () => {
        this.generating = false;
        this.toaster.Error(this.isEditing ? 'Could not update the link.' : 'Could not generate link.');
      },
    });
  }

  private copyToClipboard(text: string): void {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => this.fallbackCopy(text));
    } else {
      this.fallbackCopy(text);
    }
  }

  private fallbackCopy(text: string): void {
    const el = document.createElement('textarea');
    el.value = text;
    document.body.appendChild(el);
    el.select();
    try {
      document.execCommand('copy');
    } catch {
      /* no-op */
    }
    document.body.removeChild(el);
  }
}
